/**
 * WebVTT (W3C, https://www.w3.org/TR/webvtt1/).
 *
 * Unlike SRT this one HAS a spec, so the leniencies are named and bounded rather than
 * inherited from whatever three players happen to accept. The sections that decide the
 * behaviour below, each cited in the fixture manifest:
 *
 *   • **§4.1 file structure** — the file begins with the string `WEBVTT`, optionally followed
 *     by a space or tab and then anything. A file that does not is not WebVTT, and the sniff
 *     returns 0 rather than trying.
 *   • **§4.2 header** — everything up to the first blank line. `X-TIMESTAMP-MAP` lives here
 *     and HLS segments are unusable without it, so it is preserved verbatim in
 *     `DocumentMeta.extra` rather than parsed into a field the model does not have.
 *   • **§4.3 cue** — the identifier line is OPTIONAL and is any line before the timing line
 *     that does not contain `-->`. An identifier that looks like a number is still an
 *     identifier, NOT a cue number: WebVTT has none. It goes to `Cue.extra['id']`, and
 *     `Cue.index` stays null.
 *   • **§4.4 timings** — `[hh:]mm:ss.ttt`, hours optional, and the spec requires exactly
 *     three fraction digits. Files in the wild carry two; those are read, and the fixture
 *     records that this is leniency rather than spec.
 *   • **§4.5 cue settings** — `vertical`, `line`, `position`, `size`, `align`, `region`. All
 *     six round-trip. `line:` is the one that matters most in practice: `line:-2` lifts
 *     captions above burned-in text, and dropping it drops the caption back onto it.
 *   • **§4.6 comments** — `NOTE`, and §4.7 `STYLE`, §4.8 `REGION`.
 *   • **§5.1 default classes, §4.7 STYLE** — colour. `<c.yellow>` is the run's colour, not a
 *     class name, and so is `<c.c_ff8800>` when a `STYLE` sheet gives that class nothing but a
 *     `color`. Such a rule is taken out of the preserved sheet, because the writer writes one for
 *     every colour it emits; leaving it in would double it on every round trip.
 *   • **§6.3 cue timestamps** — `<00:00:01.000>` inside cue text. THIS is WebVTT's per-word
 *     timing carrier and one of the three product differentiators; each timestamp starts a run
 *     with `timeKind: 'word'` and `timeOrigin: 'file'`.
 *   • **§6.4 cue markup** — `<i> <b> <u> <c.class> <v Name> <lang> <ruby>/<rt>`.
 *
 * ENCODING: the spec mandates UTF-8, so the adapter decodes it as UTF-8 with
 * `EncodingInfo.method: 'declared'` and confidence 1.0. Nothing here re-decodes.
 */

import type {
  Alignment,
  Cue,
  ParseOptions,
  ParseResult,
  ParserDefinition,
  Position,
  RawEvent,
  Region,
  RunContext,
  TextLine,
  Ticks,
} from '../../../contracts/cue-model.ts';
import { MS_GRID, makeCue, originFor } from '../../core/model/build.ts';
import { recomputeTextFlags } from '../../core/model/text.ts';
import { TICKS_PER_MS } from '../../core/time/ticks.ts';
import { parseTimestamp, timeFormat } from '../../core/time/timestamp.ts';
import { Draft, safeSpan } from '../common/draft.ts';
import { readVttColorClasses } from '../common/color.ts';
import { isBlank, type SourceLine, scanLines } from '../common/lines.ts';
import { decodeRuns, parseMarkup, VTT_MARKUP } from '../common/markup.ts';

const VTT_TIME = timeFormat('vtt');
const SIGNATURE = /^WEBVTT([\t ].*)?$/;
const TIMING = /^\s*(\S+)\s*-->\s*(\S+)\s*(.*)$/;

const readTimestamp = (s: string): Ticks | null => parseTimestamp(s, VTT_TIME);

export function parseVtt(text: string, opts: ParseOptions, ctx: RunContext): ParseResult {
  const lines = scanLines(text);
  const first = lines[0];
  if (first === undefined || !SIGNATURE.test(first.text)) {
    return {
      ok: false,
      reason: 'not-recognized',
      detail:
        'line 1: a WebVTT file must begin with the string "WEBVTT" (W3C WebVTT §4.1); ' +
        `found ${JSON.stringify((first?.text ?? '').slice(0, 40))}`,
    };
  }

  const draft = new Draft({ alloc: ctx.alloc, opts, sourceFormat: 'vtt', grid: MS_GRID });

  // §4.1 — text after the signature is a free-form label. Files use it as a title.
  const label = (SIGNATURE.exec(first.text)?.[1] ?? '').trim();
  if (label !== '') {
    draft.setTitle(label.replace(/^[-–—]\s*/, ''));
    draft.addExtra('WEBVTT', label);
  }

  let i = 1;
  // §4.2 header — up to the first blank line.
  while (i < lines.length && !isBlank(lines[i] as SourceLine)) {
    const line = (lines[i] as SourceLine).text;
    const eq = line.indexOf('=');
    const colon = line.indexOf(':');
    if (eq > 0 && (colon < 0 || eq < colon)) {
      draft.addExtra(line.slice(0, eq).trim(), line.slice(eq + 1).trim());
    } else if (colon > 0) {
      draft.addExtra(line.slice(0, colon).trim(), line.slice(colon + 1).trim());
    } else {
      draft.addExtra('Header', line.trim());
    }
    i++;
  }

  const regionsByName = new Map<string, Region>();
  let ordinal = 0;
  let firstFailure: string | null = null;

  // Every block first, so every STYLE sheet is known before the first cue is read: which classes
  // are colours is a property of the whole file, and the spec puts the sheets before the cues.
  const blocks = vttBlocks(lines.slice(i));
  const colors = readVttColorClasses(blocks.filter((b) => keywordOf(b) === 'STYLE').map(sheetOf));

  for (const block of blocks) {
    const head = block[0] as SourceLine;
    const keyword = keywordOf(block);

    // §4.6 NOTE — a comment block. Preserved verbatim so a round trip does not delete the
    // author's notes. Their position relative to cues is NOT preserved; the writer re-emits
    // them after the header, and the fixture manifest records that.
    if (keyword === 'NOTE') {
      draft.addExtra(
        'NOTE',
        block
          .map((l) => l.text)
          .join('\n')
          .replace(/^NOTE\s?/, ''),
      );
      continue;
    }
    // §4.7 STYLE — a CSS block. Preserved verbatim for the same reason, less the colour rules
    // that became `primaryColor` (see the header). A sheet that held only those is gone.
    if (keyword === 'STYLE') {
      const sheet = sheetOf(block);
      const kept = colors.strip(sheet);
      if (kept !== '' || sheet === '') draft.addExtra('STYLE', kept);
      continue;
    }
    // §4.8 REGION — modelled, because Cue.region binds to it.
    if (keyword === 'REGION') {
      const region = parseRegion(block.slice(1), ctx);
      if (region !== null) {
        draft.regions.push(region);
        regionsByName.set(region.name, region);
      }
      continue;
    }

    // §4.3 — an optional identifier line, then the timing line.
    let at = 0;
    let identifier: string | null = null;
    if (!head.text.includes('-->')) {
      identifier = head.text.trim();
      at = 1;
    }
    const timingLine = block[at];
    if (timingLine === undefined || !timingLine.text.includes('-->')) {
      const where = timingLine ?? head;
      const message = `expected a WebVTT timing line like 00:01:23.456 --> 00:01:25.000, found ${JSON.stringify(where.text.slice(0, 40))}`;
      draft.diag('malformed-timestamp', message, { sourceLine: where.no });
      firstFailure ??= `line ${where.no}: ${message}`;
      continue;
    }

    const m = TIMING.exec(timingLine.text);
    const start = m === null ? null : readTimestamp(m[1] as string);
    const end = m === null ? null : readTimestamp(m[2] as string);
    if (m === null || start === null || end === null) {
      const message = `expected a WebVTT timing line like 00:01:23.456 --> 00:01:25.000, found ${JSON.stringify(timingLine.text.slice(0, 40))}`;
      draft.diag('malformed-timestamp', message, { sourceLine: timingLine.no });
      firstFailure ??= `line ${timingLine.no}: ${message}`;
      continue;
    }

    const id = ctx.alloc.cue();
    const time = safeSpan(start as number, end as number, draft, {
      cueId: id,
      sourceLine: timingLine.no,
    });
    const settings = parseSettings(m[3] ?? '');
    // A line of spaces inside a cue (see `vttBlocks`) is a row that shows nothing, not text.
    const body = block
      .slice(at + 1)
      .filter((l) => !isBlank(l))
      .map((l) => l.text)
      .join('\n');

    const markup = parseMarkup({
      text: body,
      alloc: ctx.alloc,
      caps: VTT_MARKUP,
      cueStart: time.start,
      cueEnd: time.end,
      parseTimestamp: readTimestamp,
      classColors: colors.colors,
    });
    for (const tag of markup.unknown) {
      draft.diag('malformed-tag', `unrecognized WebVTT markup ${tag} left as literal text`, {
        cueId: id,
        sourceLine: timingLine.no,
      });
    }
    const textLines = seedLeadingTiming(
      decodeRuns(markup.lines) as TextLine[],
      time.start,
      markup.wordTimed,
    );

    const region = settings.region === null ? null : (regionsByName.get(settings.region) ?? null);
    if (settings.region !== null && region === null) {
      draft.diag(
        'unknown-style-reference',
        `cue references region "${settings.region}", which this file does not define`,
        { cueId: id, sourceLine: timingLine.no },
      );
    }

    const extra: Record<string, string> = {};
    // §4.3: the identifier is NOT a cue number, even when it looks like one. Keeping it in
    // `extra` and leaving `Cue.index` null is what stops a VTT→SRT conversion from
    // renumbering a file according to identifiers that were never ordinals.
    if (identifier !== null && identifier !== '') extra['id'] = identifier;

    const raw: RawEvent | null = opts.retainRaw
      ? {
          format: 'vtt',
          fields: {
            Id: identifier ?? '',
            Start: (m[1] as string).trim(),
            End: (m[2] as string).trim(),
            Settings: (m[3] ?? '').trim(),
            Text: body,
          },
          timeFields: ['Start', 'End'],
          stale: false,
        }
      : null;

    const base = makeCue({
      id,
      time,
      lines: textLines,
      styleId: draft.defaultStyleId,
      trackId: draft.defaultTrackId,
      origin: { ...originFor(draft.inputId, 'parse', ordinal), sourceLine: timingLine.no },
      granularity: markup.wordTimed ? 'word' : 'coalesced',
    });
    const cue: Cue = {
      ...base,
      position: settings.position,
      region: region?.id ?? null,
      actor: markup.voices[0] ?? null,
      extra,
      raw,
      flags: { ...base.flags, hasRelativeTimes: markup.wordTimed },
    };
    draft.addCue(cue);
    ordinal++;
  }

  const unrolled = unrollRollingCaptions(draft.cues);
  if (unrolled !== null) draft.cues.splice(0, draft.cues.length, ...unrolled);

  if (draft.cues.length === 0 && firstFailure !== null) {
    return draft.notRecognized(firstFailure);
  }
  return draft.ok();
}

const keywordOf = (block: readonly SourceLine[]): string =>
  (block[0] as SourceLine).text.trim().split(/\s/, 1)[0] ?? '';

/** A STYLE block's sheet: every line after the `STYLE` line. */
const sheetOf = (block: readonly SourceLine[]): string =>
  block
    .slice(1)
    .map((l) => l.text)
    .join('\n');

/**
 * The file's blocks, split where WebVTT splits them: a cue's text ends at an EMPTY line (§6.1,
 * "collect a WebVTT block"). A line of spaces is not empty. It is a row of the cue that shows
 * nothing, and the cue goes on below it.
 *
 * YouTube's automatic captions depend on this. A line that starts after a pause is written
 * ` \nLike<00:00:53.480><c> what</c>…`: a blank top row, then the words being spoken. Splitting
 * at the space made those words a block of their own with no timing line, so they were dropped
 * with a malformed-timestamp diagnostic and the cue came out EMPTY while they were said.
 *
 * The old leniency stays where it was earning its keep: a line of spaces still ends the block
 * when what follows starts a new one (a timing line, an identifier above one, NOTE, STYLE or
 * REGION), so a file that separates its cues with lines of spaces reads as it did.
 */
function vttBlocks(lines: readonly SourceLine[]): readonly (readonly SourceLine[])[] {
  const out: SourceLine[][] = [];
  let current: SourceLine[] = [];
  const flush = (): void => {
    if (current.length > 0) out.push(current);
    current = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] as SourceLine;
    if (line.text === '') flush();
    else if (!isBlank(line)) current.push(line);
    else if (current.length > 0 && continuesBlock(lines, i)) current.push(line);
    else flush();
  }
  flush();
  return out;
}

/** True when the line of spaces at `at` has more of the same block below it. */
function continuesBlock(lines: readonly SourceLine[], at: number): boolean {
  let next = at + 1;
  while (next < lines.length && (lines[next] as SourceLine).text !== '') {
    if (!isBlank(lines[next] as SourceLine)) break;
    next++;
  }
  const line = lines[next];
  if (line === undefined || line.text === '') return false;
  if (line.text.includes('-->') || /^(NOTE|STYLE|REGION)(\s|$)/.test(line.text)) return false;
  return !(lines[next + 1]?.text.includes('-->') ?? false);
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Rolling captions
───────────────────────────────────────────────────────────────────────────────────────*/

/**
 * How far apart two times may be and still be one moment. YouTube writes its rolling cues
 * back to back to the millisecond; 40 ms is one frame at 25 fps, and it admits a converter
 * that rounded to frames.
 */
const SAME_MOMENT = 40 * TICKS_PER_MS;

/** A line as the viewer reads it: its runs joined, the spacing collapsed. */
const lineText = (line: TextLine): string =>
  line.runs
    .map((r) => r.text)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();

/** How many of `cur`'s first lines are `prev`'s last lines, the longest match first. */
function carriedLines(prev: readonly string[], cur: readonly string[]): number {
  for (let k = Math.min(prev.length, cur.length); k > 0; k--) {
    let same = true;
    for (let j = 0; j < k && same; j++)
      same = cur[j] !== '' && cur[j] === prev[prev.length - k + j];
    if (same) return k;
  }
  return 0;
}

/**
 * ROLLING CAPTIONS, written out once each.
 *
 * YouTube's automatic captions as VTT (what yt-dlp saves with `--write-auto-subs`) scroll like
 * a teleprompter. Each cue shows the line before above the line being spoken, and between two
 * cues a 10 ms cue holds the finished line alone:
 *
 *     00:00:02.720 --> 00:00:04.789   react a javascript library for building
 *                                     user<00:00:03.040><c> interfaces</c>…
 *     00:00:04.789 --> 00:00:04.799   user interfaces developed at facebook
 *     00:00:04.799 --> 00:00:06.789   user interfaces developed at facebook
 *                                     and<00:00:04.960><c> released</c>…
 *
 * Read cue by cue, every line is written three times: in its own cue, in the 10 ms cue and on
 * top of the next one. An SRT made from it showed every line twice and flashed each for 10 ms
 * between; a TXT repeated each line three times.
 *
 * THE RULE: a line that was on screen when the cue before ended, and is on screen again at
 * the top of the cue that starts at that moment, is the same line still showing, not a new
 * one. It is removed from the later cue. A cue left with nothing new (the 10 ms cue, or a
 * cleared screen) is dropped, and the cue it held on is extended over its time. What is left
 * is each line once, from when it starts being spoken until the next one does, with its word
 * timings (the line's first word gets its time from the cue's start, as §6.3 says).
 *
 * WHEN: only in a file that does this at nearly every boundary. Three in five boundaries must
 * carry a line before any cue is touched. Ordinary two-line captions never come close: a cue
 * that happens to open with the line the cue before ended on is a coincidence, and in such a
 * file it is left exactly as written. The `<c>` word tags are no part of the test, since
 * YouTube leaves them off a one-word line and other rolling sources never write them.
 *
 * Returns null when the file is not rolling, so the caller keeps the cues it has.
 */
function unrollRollingCaptions(cues: readonly Cue[]): Cue[] | null {
  if (cues.length < 3) return null;
  const texts = cues.map((c) => c.lines.map(lineText));
  const carries = (i: number): number => {
    const prev = cues[i - 1] as Cue;
    const cue = cues[i] as Cue;
    const gap = Math.abs((cue.time.start as number) - (prev.time.end as number));
    return gap <= SAME_MOMENT ? carriedLines(texts[i - 1] as string[], texts[i] as string[]) : 0;
  };

  let rolled = 0;
  for (let i = 1; i < cues.length; i++) if (carries(i) > 0) rolled++;
  if (rolled < 2 || rolled * 5 < (cues.length - 1) * 3) return null;

  const out: Cue[] = [];
  for (let i = 0; i < cues.length; i++) {
    const cue = cues[i] as Cue;
    const carried = i === 0 ? 0 : carries(i);
    const fresh = cue.lines.slice(carried);
    if (fresh.every((l) => lineText(l) === '')) {
      // Nothing new on screen. The line it holds was already written; the cue that showed it
      // stays up until this one ends, so nothing flashes and nothing overlaps.
      const last = out[out.length - 1];
      const touching =
        last !== undefined &&
        Math.abs((cue.time.start as number) - (last.time.end as number)) <= SAME_MOMENT;
      if (carried > 0 && touching && (cue.time.end as number) > (last.time.end as number)) {
        out[out.length - 1] = { ...last, time: { start: last.time.start, end: cue.time.end } };
      }
      continue;
    }
    if (carried === 0) {
      out.push(cue);
      continue;
    }
    const lines = seedLeadingTiming(fresh, cue.time.start, true);
    const wordTimed = lines.some((l) => l.runs.some((r) => r.time !== null));
    out.push({
      ...cue,
      lines,
      granularity: wordTimed ? 'word' : 'coalesced',
      flags: recomputeTextFlags(cue, lines),
      raw: cue.raw === null ? null : { ...cue.raw, stale: true },
    });
  }
  return out;
}

/**
 * §6.3 — text BEFORE the first cue timestamp is displayed from the cue's own start.
 *
 * The spec says so, so the leading run's timing is a fact about the file rather than an
 * interpolation, and it gets `timeOrigin: 'file'` accordingly. Seeding it matters for more
 * than tidiness: without it a word-timed cue reports `wordTimingCoverage: 'partial'`, which
 * disables the karaoke and Enhanced-LRC export paths for a cue that is fully timed in the
 * source.
 *
 * With MORE than one leading run — styled lead-in text — the interval covers several runs and
 * splitting it would be synthesis rather than reading, so they are left untimed and the cue is
 * honestly 'partial'. The writer emits a stamp wherever one exists, so the round trip is exact
 * either way.
 */
function seedLeadingTiming(
  lines: readonly TextLine[],
  cueStart: Ticks,
  wordTimed: boolean,
): TextLine[] {
  if (!wordTimed) return [...lines];
  const flat = lines.flatMap((l) => l.runs);
  const firstTimed = flat.findIndex((r) => r.time !== null);
  if (firstTimed !== 1) return [...lines];
  const target = flat[0];
  const nextTime = flat[1]?.time ?? null;
  if (target === undefined || nextTime === null || target.time !== null) return [...lines];
  if ((nextTime.start as number) <= (cueStart as number)) return [...lines];

  return lines.map((line, i) =>
    i === 0 && line.runs[0] === target
      ? {
          ...line,
          runs: [
            {
              ...target,
              time: { start: cueStart, end: nextTime.start },
              timeKind: 'word' as const,
              timeOrigin: 'file' as const,
            },
            ...line.runs.slice(1),
          ],
        }
      : line,
  );
}

/*───────────────────────────────────────────────────────────────────────────────────────
  §4.5 cue settings
───────────────────────────────────────────────────────────────────────────────────────*/

interface Settings {
  readonly position: Position | null;
  readonly region: string | null;
}

const EMPTY_POSITION: Position = {
  alignment: null,
  point: null,
  move: null,
  rotationOrigin: null,
  clip: null,
  sizePct: null,
  vertical: null,
  line: null,
  positionPct: null,
};

export function parseSettings(raw: string): Settings {
  const text = raw.trim();
  if (text === '') return { position: null, region: null };

  let position: Position = EMPTY_POSITION;
  let region: string | null = null;
  let touched = false;
  let align: 'start' | 'center' | 'end' | 'left' | 'right' | null = null;

  for (const token of text.split(/\s+/)) {
    const colon = token.indexOf(':');
    if (colon <= 0) continue;
    const key = token.slice(0, colon).toLowerCase();
    const value = token.slice(colon + 1);
    switch (key) {
      case 'vertical': {
        if (value === 'rl' || value === 'lr') {
          position = { ...position, vertical: value };
          touched = true;
        }
        break;
      }
      case 'line': {
        // `line:-2` is a snap-to-lines INTEGER counted from the bottom; `line:90%` is a
        // percentage. A normalized Point cannot carry the first, which is why Position.line
        // exists as its own field.
        const [head, tail] = value.split(',', 2);
        const percent = (head ?? '').endsWith('%');
        const n = Number.parseFloat(percent ? (head as string).slice(0, -1) : (head ?? ''));
        if (!Number.isFinite(n)) break;
        const lineAlign = tail === 'start' || tail === 'center' || tail === 'end' ? tail : null;
        position = {
          ...position,
          line: { value: n, unit: percent ? 'percent' : 'line', align: lineAlign },
        };
        touched = true;
        break;
      }
      case 'position': {
        const [head, tail] = value.split(',', 2);
        const n = Number.parseFloat((head ?? '').replace('%', ''));
        if (!Number.isFinite(n)) break;
        const posAlign =
          tail === 'line-left' || tail === 'center' || tail === 'line-right' ? tail : null;
        position = { ...position, positionPct: { value: n, align: posAlign } };
        touched = true;
        break;
      }
      case 'size': {
        const n = Number.parseFloat(value.replace('%', ''));
        if (!Number.isFinite(n)) break;
        position = { ...position, sizePct: n };
        touched = true;
        break;
      }
      case 'align': {
        if (
          value === 'start' ||
          value === 'center' ||
          value === 'end' ||
          value === 'left' ||
          value === 'right'
        ) {
          align = value;
          touched = true;
        }
        break;
      }
      case 'region': {
        region = value;
        break;
      }
      default:
        break;
    }
  }

  if (align !== null) {
    position = { ...position, alignment: alignmentFor(align, position.line) };
  }
  return { position: touched ? position : null, region };
}

/**
 * WebVTT `align:` plus `line:` → the model's 9-cell anchor.
 *
 * The two are orthogonal in WebVTT and fused in the numpad, so this is the one lossy step in
 * the settings round trip — and it is not lossy in practice, because `Position.line` keeps the
 * exact `line:` value alongside and the writer re-derives `align:` from the column alone.
 */
export function alignmentFor(
  align: 'start' | 'center' | 'end' | 'left' | 'right',
  line: Position['line'],
): Alignment {
  const col = align === 'start' || align === 'left' ? 0 : align === 'center' ? 1 : 2;
  // `line:0` / a small percentage puts the box at the TOP; the default and everything else
  // is the bottom row. `line:-1` is the bottom line, counted from the bottom.
  const top =
    line !== null &&
    ((line.unit === 'percent' && line.value <= 25) ||
      (line.unit === 'line' && line.value >= 0 && line.value <= 1));
  return ((top ? 6 : 0) + col + 1) as Alignment;
}

/*───────────────────────────────────────────────────────────────────────────────────────
  §4.8 REGION
───────────────────────────────────────────────────────────────────────────────────────*/

function parseRegion(body: readonly SourceLine[], ctx: RunContext): Region | null {
  const settings = new Map<string, string>();
  for (const line of body) {
    for (const token of line.text.trim().split(/\s+/)) {
      const colon = token.indexOf(':');
      if (colon > 0) settings.set(token.slice(0, colon).toLowerCase(), token.slice(colon + 1));
    }
  }
  const name = settings.get('id');
  if (name === undefined) return null;
  const anchor = pointOf(settings.get('regionanchor') ?? '0%,100%');
  const viewport = pointOf(settings.get('viewportanchor') ?? '0%,100%');
  const width = Number.parseFloat((settings.get('width') ?? '100%').replace('%', ''));
  const nLines = Number.parseInt(settings.get('lines') ?? '3', 10);
  return {
    id: ctx.alloc.region(),
    name,
    anchor,
    viewportAnchor: viewport,
    widthPct: Number.isFinite(width) ? width : 100,
    lines: Number.isFinite(nLines) ? nLines : 3,
    scroll: settings.get('scroll') === 'up' ? 'up' : 'none',
  };
}

function pointOf(raw: string): { readonly x: number; readonly y: number } {
  const [x, y] = raw.split(',', 2);
  const px = Number.parseFloat((x ?? '0').replace('%', ''));
  const py = Number.parseFloat((y ?? '0').replace('%', ''));
  return {
    x: Number.isFinite(px) ? px / 100 : 0,
    y: Number.isFinite(py) ? py / 100 : 1,
  };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Detection
───────────────────────────────────────────────────────────────────────────────────────*/

/** The signature is mandatory and unambiguous, so detection is exact: 1.0 or 0. Nothing
 *  else in the read set begins with `WEBVTT`. */
export function sniffVtt(text: string, _filename: string | null): number {
  const first = text.split('\n', 1)[0] ?? '';
  return SIGNATURE.test(first.replace(/\r$/, '')) ? 1 : 0;
}

export const vttParser: ParserDefinition = {
  format: 'vtt',
  sniff: sniffVtt,
  requiresFramerate: false,
  parse: parseVtt,
};
